import PropTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import dayjs from 'dayjs';

/**
 * Delivery / read state for a message YOU sent.
 *
 * Two ticks, the convention every messaging app uses:
 *   muted  = delivered, the client has not opened the conversation since it was sent
 *   accent = read, the client opened the conversation after it arrived
 *
 * Only ever rendered on your own messages — a tick on an incoming message would be
 * telling the coach whether they themselves had read it, which is not information.
 *
 * `isRead` is written by the backend when the recipient opens the room:
 * ChatRoomService.markRoomRead for the mobile app, ChatService.getMessages for the
 * panel. `readAt` is stamped on the first read only, so the tooltip shows when they
 * actually saw it rather than the last time the thread was reopened.
 *
 * Messages read BEFORE readAt existed have isRead: true and readAt: null — those show
 * the read tick with a plain "Read" tooltip rather than inventing a time.
 */
const ReadReceipt = ({ isRead, readAt, size = 15 }) => {
  const label = isRead ? (readAt ? `Read ${dayjs(readAt).format('DD MMM YYYY, HH:mm')}` : 'Read') : 'Delivered';

  return (
    <Tooltip title={label} placement="top" arrow>
      <Box
        component="span"
        aria-label={label}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          ml: 0.5,
          verticalAlign: 'middle',
          // These ticks sit on the PURPLE own-message bubble, so the read state cannot
          // use the accent: light purple on purple is near-invisible against the muted
          // white of delivered, and the two states became indistinguishable — which is
          // the whole job of the indicator.
          //
          // Blue for read is also the convention people already read without being told
          // (WhatsApp, Telegram). It is deliberately NOT a palette accent: this is a
          // status signal on a fixed-colour bubble, not brand decoration.
          color: isRead ? '#5EC8F5' : 'rgba(255,255,255,0.55)'
        }}
      >
        <svg width={size + 5} height={size} viewBox="0 0 20 13" fill="none" aria-hidden="true" focusable="false">
          <path
            d="M1 7.2 3.9 10.1 10 3.6"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8 7.2 10.9 10.1 19 1.9"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    </Tooltip>
  );
};

ReadReceipt.propTypes = {
  isRead: PropTypes.bool,
  readAt: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  size: PropTypes.number
};

export default ReadReceipt;
